/**
 * Reading identifiers the way people paste them: bare, prefixed, or as a link.
 *
 * Each reader returns the canonical form a lookup service accepts, or null — and a
 * null is a line the widget reports back ("not a DOI") rather than sends anywhere.
 */

const RE_LINE_BREAK = /\r?\n/;

/** Non-empty, trimmed lines, duplicates kept (the caller decides what a duplicate means). */
export function inputLines(text: string): string[] {
  return text
    .split(RE_LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/* -------------------------------------------------------------------- DOI */

const RE_DOI_URL_PREFIX = /^(?:https?:\/\/)?(?:dx\.)?doi\.org\//i;
const RE_DOI_SCHEME_PREFIX = /^doi:\s*/i;
const RE_DOI_SHAPE = /^10\.\d{4,9}\/\S+$/;
const RE_TRAILING_PUNCT = /[.,;:)\]}>"']+$/;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** "https://doi.org/10.1038/nphys1170." → "10.1038/nphys1170" */
export function normalizeDoi(raw: string): string | null {
  const stripped = raw
    .trim()
    .replace(RE_DOI_URL_PREFIX, "")
    .replace(RE_DOI_SCHEME_PREFIX, "")
    .replace(RE_TRAILING_PUNCT, "");
  const decoded = safeDecode(stripped);
  return RE_DOI_SHAPE.test(decoded) ? decoded : null;
}

/* ----------------------------------------------------------------- PubMed */

export type PubmedId =
  | { kind: "pmid"; id: string }
  | { kind: "pmcid"; id: string };

const RE_PMID_BARE = /^(?:pmid\s*[:#]?\s*)?(\d{1,8})$/i;
const RE_PMCID = /^(?:pmcid\s*[:#]?\s*)?(?:pmc)(\d{1,9})$/i;
const RE_PUBMED_URL = /pubmed(?:\.ncbi\.nlm\.nih\.gov|\/)\/?(\d{1,8})\b/i;
const RE_PMC_URL = /\/(?:pmc\/)?articles\/pmc(\d{1,9})\b/i;
const RE_EUROPE_PMC_MED =
  /europepmc\.org\/(?:abstract|article)\/med\/(\d{1,8})\b/i;

/**
 * 29083010, PMID: 29083010, PMC5334499, or a PubMed / PMC / Europe PMC link.
 * PMIDs are up to eight digits today; a longer number is reported, not guessed at.
 */
export function parsePubmedId(raw: string): PubmedId | null {
  const line = raw.trim();
  const pmc = RE_PMCID.exec(line) ?? RE_PMC_URL.exec(line);
  if (pmc) {
    return { kind: "pmcid", id: `PMC${pmc[1]}` };
  }
  const pmid =
    RE_PMID_BARE.exec(line) ??
    RE_PUBMED_URL.exec(line) ??
    RE_EUROPE_PMC_MED.exec(line);
  if (pmid) {
    return { kind: "pmid", id: String(Number(pmid[1])) };
  }
  return null;
}

/* -------------------------------------------------------------------- URL */

const RE_HAS_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
// "mailto:", "tel:", "javascript:" — a scheme with no "//" (a port is digits, so
// "example.com:8080" is not one).
const RE_OTHER_SCHEME = /^[a-z][a-z0-9+.-]*:(?!\/\/|\d)/i;
const RE_DOMAIN_LIKE = /^[^\s/]+\.[a-z]{2,}(?:[/:?#]|$)/i;
const RE_WHITESPACE = /\s/;

/**
 * An http(s) address, with https:// added to a bare "www.example.com/page".
 * Anything else — mailto:, a file path, words — is null.
 */
export function normalizeWebUrl(raw: string): string | null {
  const line = raw.trim();
  if (line === "" || RE_WHITESPACE.test(line)) {
    return null;
  }
  let candidate = line;
  if (!RE_HAS_SCHEME.test(line)) {
    if (RE_OTHER_SCHEME.test(line) || !RE_DOMAIN_LIKE.test(line)) {
      return null;
    }
    candidate = `https://${line}`;
  }
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}
